import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { FaUpload, FaTimes } from "react-icons/fa";

const CreateXrayPage = () => {
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [gender, setGender] = useState("");
  const [preparation, setPreparation] = useState("");
  const [reportTime, setReportTime] = useState("");
  const [image, setImage] = useState(null);
  const [diagnosticsList, setDiagnosticsList] = useState([]);
  const [selectedDiagnostics, setSelectedDiagnostics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  const [debugInfo, setDebugInfo] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const navigate = useNavigate();

  // Add axios interceptors for debugging
  useEffect(() => {
    // Request interceptor
    axios.interceptors.request.use(
      (config) => {
        console.log("📤 Sending Request:", {
          url: config.url,
          method: config.method,
          headers: config.headers,
          data: config.data instanceof FormData ? "FormData" : config.data,
        });

        if (config.data instanceof FormData) {
          console.log("FormData entries:");
          for (let pair of config.data.entries()) {
            console.log(pair[0] + ": " + pair[1]);
          }
        }

        return config;
      },
      (error) => {
        console.error("❌ Request Error:", error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    axios.interceptors.response.use(
      (response) => {
        console.log("📥 Received Response:", {
          status: response.status,
          data: response.data,
        });
        return response;
      },
      (error) => {
        console.error("❌ Response Error Details:", {
          message: error.message,
          code: error.code,
          response: error.response,
          request: error.request,
        });

        if (error.code === "ERR_NETWORK") {
          console.error("🌐 Network Error Detected!");
          console.error("Check: Internet connection, CORS, Server status");
        }

        return Promise.reject(error);
      }
    );
  }, []);

  // Fetch diagnostics list
  useEffect(() => {
    const fetchDiagnostics = async () => {
      try {
        const token = localStorage.getItem("authToken");

        setLoadingDiagnostics(true);

        const response = await axios.get(
          "https://api.elthiumhealth.com/api/admin/alldiagnostics",
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            timeout: 10000,
          }
        );

        console.log("Diagnostics API Response:", response.data);

        if (response.data?.data) {
          setDiagnosticsList(response.data.data);
          setDebugInfo(
            `Loaded ${response.data.data.length} diagnostics`
          );
        } else {
          setDiagnosticsList([]);
          setDebugInfo("No diagnostics found");
        }
      } catch (error) {
        console.error(
          "❌ Error fetching diagnostics:",
          error.response?.data || error.message
        );

        let errorMsg = "Failed to load diagnostics list";

        if (
          error.code === "ERR_NETWORK" ||
          error.code === "ECONNABORTED"
        ) {
          errorMsg =
            error.code === "ECONNABORTED"
              ? "Request timed out while loading diagnostics."
              : "Network error while loading diagnostics. Check your internet connection.";
        } else if (error.response) {
          errorMsg = `Server error: ${error.response.status}`;
        }

        setDebugInfo(errorMsg);

        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
        });

        setDiagnosticsList([]);
      } finally {
        setLoadingDiagnostics(false);
      }
    };

    fetchDiagnostics();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log("Selected file:", {
        name: file.name,
        type: file.type,
        size: (file.size / 1024 / 1024).toFixed(2) + " MB",
      });

      // Validate file type
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      if (!validTypes.includes(file.type)) {
        Swal.fire({
          icon: "warning",
          title: "Invalid File Type",
          text: "Please upload an image (JPEG, JPG, PNG, or WebP)",
        });
        e.target.value = "";
        setImage(null);
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        Swal.fire({
          icon: "warning",
          title: "File Too Large",
          text: "Image size should be less than 5MB",
        });
        e.target.value = "";
        setImage(null);
        return;
      }

      setImage(file);
      setDebugInfo(`Image selected: ${file.name}`);
    }
  };

  // Diagnostic select/unselect handlers
  const handleDiagnosticSelect = (diagnosticId) => {
    if (selectedDiagnostics.includes(diagnosticId)) {
      // Remove if already selected
      setSelectedDiagnostics(selectedDiagnostics.filter(id => id !== diagnosticId));
    } else {
      // Add if not selected
      setSelectedDiagnostics([...selectedDiagnostics, diagnosticId]);
    }
  };

  const handleRemoveDiagnostic = (diagnosticId) => {
    setSelectedDiagnostics(selectedDiagnostics.filter(id => id !== diagnosticId));
  };

  const handleSelectAll = () => {
    if (selectedDiagnostics.length === diagnosticsList.length) {
      // Deselect all
      setSelectedDiagnostics([]);
    } else {
      // Select all
      setSelectedDiagnostics(diagnosticsList.map(d => d._id));
    }
  };

  // Filter diagnostics based on search
  const filteredDiagnostics = diagnosticsList.filter(diagnostic => {
    const name = diagnostic.name || `Diagnostic ${diagnostic._id.substring(0, 8)}`;
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Get selected diagnostic names for display
  const getSelectedDiagnosticNames = () => {
    return selectedDiagnostics.map(id => {
      const diagnostic = diagnosticsList.find(d => d._id === id);
      return diagnostic ? (diagnostic.name || `Diagnostic ${id.substring(0, 8)}`) : "";
    });
  };

  // Test API function
  const testAPI = async () => {
    try {
      const token = localStorage.getItem("authToken");

      if (!token) {
        Swal.fire("Authentication Error", "Please log in again.", "warning");
        return;
      }

      console.log("🧪 Testing API connection...");

      // Test 1: POST diagnostics API
      const testResponse = await axios.get(
        "https://api.elthiumhealth.com/api/admin/alldiagnostics",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          timeout: 5000,
        }
      );

      console.log("✅ Diagnostics test successful:", testResponse.status);

      // Test 2: POST create-scan API using FormData
      const testFormData = new FormData();

      testFormData.append("title", "Test Scan");
      testFormData.append("price", "1000");
      testFormData.append("gender", "Both");
      testFormData.append("preparation", "Test preparation");
      testFormData.append("reportTime", "24 hours");

      if (selectedDiagnostics.length > 0) {
        testFormData.append(
          "diagnosticIds",
          JSON.stringify(selectedDiagnostics)
        );
      }

      console.log("Testing POST with FormData...");

      const postResponse = await axios.post(
        "https://api.elthiumhealth.com/api/admin/create-scan",
        testFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 10000,
        }
      );

      console.log("✅ Create scan test response:", postResponse.data);

      Swal.fire({
        icon: "success",
        title: "API Test Successful",
        text: `Diagnostics: ${testResponse.status}, Scan: ${postResponse.data?.success ? "Success" : "Request completed"
          }`,
      });
    } catch (error) {
      console.error(
        "❌ API Test failed:",
        error.response?.data || error.message
      );

      let errorDetails = "";

      if (error.response) {
        errorDetails = `Status: ${error.response.status}, Message: ${error.response.data?.message || error.response.statusText
          }`;
      } else if (error.request) {
        errorDetails = "No response received. Check server status and CORS.";
      } else {
        errorDetails = error.message;
      }

      Swal.fire({
        icon: "error",
        title: "API Test Failed",
        text: errorDetails,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const validationErrors = [];

    if (!title.trim()) validationErrors.push("Title is required");
    if (!price || isNaN(price)) validationErrors.push("Valid price is required");
    if (!gender) validationErrors.push("Gender is required");
    if (!preparation.trim()) {
      validationErrors.push("Preparation instructions are required");
    }
    if (!reportTime.trim()) validationErrors.push("Report time is required");
    if (!image) validationErrors.push("Image is required");

    if (selectedDiagnostics.length === 0) {
      validationErrors.push("At least one diagnostic center is required");
    }

    if (validationErrors.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        html: validationErrors.map((err) => `<p>• ${err}</p>`).join(""),
      });
      return;
    }

    const token = localStorage.getItem("authToken");

    if (!token) {
      Swal.fire({
        icon: "warning",
        title: "Authentication Required",
        text: "Please log in again to continue.",
      });
      return;
    }

    const formData = new FormData();

    formData.append("title", title.trim());
    formData.append("price", String(parseFloat(price)));
    formData.append("gender", gender);
    formData.append("preparation", preparation.trim());
    formData.append("reportTime", reportTime.trim());
    formData.append("image", image);
    formData.append("diagnosticIds", JSON.stringify(selectedDiagnostics));

    console.log("📝 FormData Contents:");

    for (const [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }

    try {
      setLoading(true);
      setDebugInfo("Sending request...");

      console.log(
        "🚀 Submitting to:",
        "https://api.elthiumhealth.com/api/admin/create-scan"
      );
      console.log("Selected Diagnostics:", selectedDiagnostics);

      const response = await axios.post(
        "https://api.elthiumhealth.com/api/admin/create-scan",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 60000,
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );

              console.log(`📤 Upload progress: ${percentCompleted}%`);
              setDebugInfo(`Uploading: ${percentCompleted}%`);
            }
          },
        }
      );

      console.log("✅ Response received:", response.data);

      if (response.data?.success) {
        setDebugInfo("✅ Scan created successfully!");

        await Swal.fire({
          icon: "success",
          title: "Success!",
          text:
            response.data.message ||
            "X-ray/Scan created successfully!",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });

        handleReset();

        setTimeout(() => {
          navigate("/xraylist");
        }, 2000);
      } else {
        throw new Error(
          response.data?.message || "API returned unsuccessful"
        );
      }
    } catch (error) {
      console.error(
        "❌ Submission Error:",
        error.response?.data || error.message
      );

      let errorTitle = "Error";
      let errorMessage = "Failed to create X-ray/Scan";
      let errorDetails = "";

      if (error.response) {
        errorTitle = `Server Error (${error.response.status})`;
        errorMessage =
          error.response.data?.message || "Server error occurred";

        errorDetails = `Status: ${error.response.status}\nData: ${JSON.stringify(
          error.response.data
        )}`;

        console.error("Response error details:", {
          status: error.response.status,
          data: error.response.data,
        });
      } else if (error.request) {
        errorTitle = "Network Error";
        errorMessage = "No response received from server";

        errorDetails =
          "Possible causes:\n• Server is down\n• CORS issue\n• Network connection problem\n• Request timeout";

        console.error("No response received:", error.request);
      } else {
        errorMessage = error.message || "Unknown error occurred";
        errorDetails = error.code || "No error code";
      }

      setDebugInfo(`❌ Error: ${errorMessage}`);

      // Use text instead of injecting server error content into HTML.
      Swal.fire({
        icon: "error",
        title: errorTitle,
        text: `${errorMessage}\n\n${errorDetails}`,
        confirmButtonText: "OK",
        confirmButtonColor: "#3b82f6",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setTitle("");
    setPrice("");
    setGender("");
    setPreparation("");
    setReportTime("");
    setImage(null);
    setSelectedDiagnostics([]);
    setSearchTerm("");
    setDebugInfo("Form reset");
  };

  const logFormState = () => {
    console.log("📋 Current Form State:", {
      title,
      price,
      gender,
      preparation,
      reportTime,
      image: image ? image.name : "No image",
      selectedDiagnostics,
      selectedCount: selectedDiagnostics.length,
      diagnosticsCount: diagnosticsList.length,
    });
    Swal.fire({
      icon: "info",
      title: "Form State Logged",
      text: "Check browser console for details",
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-800">Create New X-ray/Scan</h1>
          <p className="text-gray-600 mt-2">Add a new diagnostic scan with multiple diagnostic centers</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Form Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-white">Create New X-ray/Scan</h1>
                <p className="text-blue-100 mt-1">Add a new diagnostic scan to the system</p>
              </div>
              <button
                type="button"
                onClick={testAPI}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Test API Connection
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-8">
            {/* Title & Price Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scan Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="E.g., Chest X-ray, MRI Brain, CT Scan"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="Enter the price"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Enter 0 for free scan</p>
              </div>
            </div>

            {/* Gender & Report Time Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors bg-white"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Both">Both</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Report Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  value={reportTime}
                  onChange={(e) => setReportTime(e.target.value)}
                  placeholder="E.g., Within 24 hours, Same day, 48 hours"
                  required
                />
              </div>
            </div>

            {/* Diagnostic Selection - MULTI SELECT */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Diagnostic Centers <span className="text-red-500">*</span>
                <span className="text-sm text-gray-500 font-normal ml-2">
                  (Select one or multiple diagnostic centers)
                </span>
              </label>

              {/* Selected Diagnostics Preview */}
              {selectedDiagnostics.length > 0 && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-blue-800">
                      Selected ({selectedDiagnostics.length}):
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedDiagnostics([])}
                      className="text-xs text-red-600 hover:text-red-800 font-medium"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {getSelectedDiagnosticNames().map((name, index) => (
                      <div
                        key={selectedDiagnostics[index]}
                        className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                      >
                        <span>{name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveDiagnostic(selectedDiagnostics[index])}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          <FaTimes className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Search and Select All */}
              <div className="mb-4 flex gap-3">
                <div className="flex-1">
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search diagnostic centers..."
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap"
                >
                  {selectedDiagnostics.length === diagnosticsList.length ? "Deselect All" : "Select All"}
                </button>
              </div>

              {/* Diagnostics List */}
              {loadingDiagnostics ? (
                <div className="p-6 border border-gray-300 rounded-lg bg-gray-50">
                  <div className="flex items-center justify-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <div className="text-gray-600">Loading diagnostic centers...</div>
                  </div>
                </div>
              ) : diagnosticsList.length > 0 ? (
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  <div className="max-h-60 overflow-y-auto">
                    {filteredDiagnostics.length > 0 ? (
                      <div className="divide-y divide-gray-200">
                        {filteredDiagnostics.map((diagnostic) => {
                          const isSelected = selectedDiagnostics.includes(diagnostic._id);
                          const diagnosticName = diagnostic.name || `Diagnostic ${diagnostic._id.substring(0, 8)}`;
                          return (
                            <div
                              key={diagnostic._id}
                              className={`p-4 cursor-pointer transition-colors ${isSelected ? "bg-blue-50" : "hover:bg-gray-50"
                                }`}
                              onClick={() => handleDiagnosticSelect(diagnostic._id)}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-5 h-5 border rounded flex items-center justify-center ${isSelected
                                  ? "bg-blue-600 border-blue-600"
                                  : "border-gray-300"
                                  }`}>
                                  {isSelected && (
                                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">{diagnosticName}</div>
                                  {diagnostic.address && (
                                    <div className="text-sm text-gray-500 mt-1">{diagnostic.address}</div>
                                  )}
                                </div>
                                <div className={`px-2 py-1 rounded text-xs font-medium ${isSelected
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-gray-100 text-gray-800"
                                  }`}>
                                  {isSelected ? "Selected" : "Click to select"}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-6 text-center text-gray-500">
                        No diagnostic centers found matching your search
                      </div>
                    )}
                  </div>
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                    <div className="flex justify-between items-center text-sm text-gray-600">
                      <span>
                        Showing {filteredDiagnostics.length} of {diagnosticsList.length} centers
                      </span>
                      <span>
                        {selectedDiagnostics.length} selected
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6 border border-red-300 rounded-lg bg-red-50">
                  <div className="text-center">
                    <div className="text-red-600 font-medium">No diagnostic centers available</div>
                    <p className="text-sm text-red-500 mt-1">
                      Please check API connection or add diagnostic centers first.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Preparation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preparation Instructions <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                value={preparation}
                onChange={(e) => setPreparation(e.target.value)}
                placeholder="Enter patient preparation instructions, fasting requirements, etc."
                rows="4"
                required
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scan Image <span className="text-red-500">*</span>
              </label>
              <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${image
                ? "border-green-500 bg-green-50"
                : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                }`}>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  id="image-upload"
                  onChange={handleImageChange}
                  required
                />
                <label htmlFor="image-upload" className="cursor-pointer">
                  <div className="flex flex-col items-center justify-center space-y-3">
                    <div className="p-3 bg-blue-100 rounded-full">
                      <FaUpload className="text-2xl text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        {image ? "Image Selected" : "Click to upload scan image"}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Upload an image (JPEG, JPG, PNG, WebP) • Max 5MB
                      </p>
                    </div>
                  </div>
                </label>
                {image && (
                  <div className="mt-4 p-3 bg-white rounded-lg border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-600">
                            {image.name.split('.').pop().toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700 truncate max-w-xs">
                            {image.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(image.size / 1024 / 1024).toFixed(2)} MB • {image.type}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setImage(null);
                          document.getElementById('image-upload').value = "";
                        }}
                        className="text-red-500 hover:text-red-700 text-sm font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-blue-800">Important Information</h4>
                  <ul className="mt-2 text-xs text-blue-700 space-y-1">
                    <li>• All fields marked with <span className="font-medium">*</span> are required</li>
                    <li>• <span className="font-medium">Gender</span> field determines for whom the scan is applicable</li>
                    <li>• <span className="font-medium">Price</span> can be 0 for free scans</li>
                    <li>• <span className="font-medium">Report Time</span> indicates when results will be available</li>
                    <li>• <span className="font-medium">Diagnostic Centers</span> - You can select multiple centers</li>
                    <li>• <span className="font-medium">Preparation</span> should include any special instructions for patients</li>
                    <li>• Supported image formats: JPEG, JPG, PNG, WebP (Max 5MB)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-between items-center pt-6 border-t border-gray-200">
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={logFormState}
                  className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  Debug Info
                </button>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Reset Form
                </button>

                <button
                  type="submit"
                  disabled={loading || selectedDiagnostics.length === 0}
                  className={`px-8 py-3 rounded-lg text-white font-medium flex items-center gap-2 ${loading || selectedDiagnostics.length === 0
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                    } transition-colors shadow-sm`}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating Scan...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                      </svg>
                      Create Scan ({selectedDiagnostics.length} centers)
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Network Status & Debug Info */}
        <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full">
            <div className={`w-2 h-2 rounded-full ${navigator.onLine ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-sm text-gray-600">
              Network: {navigator.onLine ? 'Online' : 'Offline'}
            </span>
          </div>

          {debugInfo && (
            <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
              <span className="font-medium">Status:</span> {debugInfo}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateXrayPage;